import { useEffect, useState } from 'react';
import { ReactComponent as MoreIcon } from '../../../../assets/svg/kebab.svg';
import { ReactComponent as ChevronDownIcon } from '../../../../assets/svg/chevron-down.svg';
import { WEIGHT_UNIT } from '../../../../types/enum';
import { convertWeight } from '../../../../lib/utils';
import Note from './common/note';

const HiitItem = ({ workoutTracker, openMenu, preference }) => {
  const [logs, setLogs] = useState([]);
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    const deepCopy = workoutTracker.logs.map((exercise) => ({
      ...exercise,
      sets: [...exercise.sets],
    }));
    const groupedExercise = deepCopy.reduce((acc, cur) => {
      const group = acc.find(
        (exercise) => exercise.groupId && exercise.groupId === cur.groupId
      );
      if (group) {
        group.name.push(cur.name);
        cur.sets.forEach((set, i) => {
          group.sets[i].push(set);
        });
      } else if (cur.groupId) {
        acc.push({
          name: [cur.name],
          sets: cur.sets.map((set) => [set]),
          groupId: cur.groupId,
          type: cur.type,
        });
      } else {
        acc.push(cur);
      }
      return acc;
    }, []);
    setLogs(groupedExercise);
  }, [workoutTracker]);

  const handleOpen = () => {
    setIsOpen(!isOpen);
  };

  return (
    <div className='bg-secondary rounded-2xl p-4'>
      <div className='flex justify-between items-center gap-2 mb-4'>
        <div className='flex items-center gap-2'>
          <span className='text-white text-xs px-2 py-1 border border-tertiary rounded-full'>
            {workoutTracker.type}
          </span>
          <div className='text-white font-bold line-clamp-1'>
            {workoutTracker.workout}
          </div>
        </div>
        <div className='text-white'>
          <MoreIcon
            className='size-5'
            onClick={openMenu}
          />
        </div>
      </div>
      <div
        className={`transition-all duration-300 ease-in-out ${
          isOpen
            ? 'space-y-4 max-h-screen opacity-100 overflow-y-auto mb-4'
            : 'max-h-0 opacity-0 overflow-hidden'
        }`}
      >
        <div className='space-y-2'>
          <div className='text-white text-sm bg-tertiary px-3 py-2 rounded-xl'>
            <div className='flex items-center justify-between gap-2 mb-2'>
              <div>{workoutTracker.logs.length} exercises</div>
              <div>{workoutTracker.cycle} cycles</div>
            </div>
            {workoutTracker.restCycle && (
              <div className='flex items-center justify-between gap-2 mb-2'>
                <div>rest between cycles</div>
                <div>{workoutTracker.restCycle} s</div>
              </div>
            )}
            {workoutTracker.restExercise && (
              <div className='flex items-center justify-between gap-2 mb-2'>
                <div>rest between exercises</div>
                <div>{workoutTracker.restExercise} s</div>
              </div>
            )}
          </div>
          {logs.map((log, i) => (
            <div key={i}>
              <div className='text-white flex items-center gap-2 text-sm'>
                {log.groupId ? (
                  <div className='w-full'>
                    <div className='flex items-center justify-between gap-2 mb-2'>
                      <div className='flex items-center gap-2'>
                        <span>{log.name.join(' & ')}</span>
                        <span
                          className={`border rounded-full size-5 flex items-center justify-center ${
                            log.type === 'superset'
                              ? 'border-protein bg-protein/20'
                              : 'border-carbs bg-carbs/20'
                          }`}
                        >
                          {log.type === 'superset' ? 'S' : 'C'}
                        </span>
                      </div>
                      <div>{log.sets.length} sets</div>
                    </div>
                    <div className='rounded-xl px-3 py-2 bg-tertiary'>
                      {log.sets.map((set, i) => (
                        <div key={i}>
                          <div>set {i + 1}</div>
                          <div className='divide-y-[1px] divide-white/10'>
                            {set.map((s, j) => (
                              <div
                                key={j}
                                className='flex items-center justify-end gap-2'
                              >
                                <div>{log.name[j]}</div>
                                <div className='flex items-center gap-2'>
                                  {typeof s.weight === 'number' &&
                                    s.weight > 0 && (
                                      <div>
                                        {preference?.weightUnit ===
                                        WEIGHT_UNIT.LB
                                          ? convertWeight(
                                              s.weight,
                                              WEIGHT_UNIT.KG,
                                              WEIGHT_UNIT.LB
                                            ).toFixed(1)
                                          : s.weight.toFixed(1)}{' '}
                                        {preference?.weightUnit?.toLowerCase()}
                                      </div>
                                    )}
                                  {typeof s.weight === 'number' &&
                                    s.weight > 0 && <div>x</div>}
                                  <div>
                                    {s.count} {s.unit.toLowerCase()}
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div className='w-full'>
                    <div className='flex items-center justify-between gap-2 mb-2'>
                      <div>{log.name}</div>
                      <div>{log.sets.length} sets</div>
                    </div>
                    <div className='rounded-xl px-3 py-2 bg-tertiary divide-y-[1px] divide-white/10'>
                      {log.sets.map((set, i) => (
                        <div
                          key={i}
                          className='flex items-center justify-between'
                        >
                          <div>set {i + 1}</div>
                          <div className='flex items-center gap-2'>
                            {typeof set.weight === 'number' &&
                              set.weight > 0 && (
                                <div>
                                  {preference?.weightUnit === WEIGHT_UNIT.LB
                                    ? convertWeight(
                                        set.weight,
                                        WEIGHT_UNIT.KG,
                                        WEIGHT_UNIT.LB
                                      ).toFixed(1)
                                    : set.weight.toFixed(1)}{' '}
                                  {preference?.weightUnit?.toLowerCase()}
                                </div>
                              )}
                            {typeof set.weight === 'number' &&
                              set.weight > 0 && <div>x</div>}
                            <div>
                              {set.count} {set.unit.toLowerCase()}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
        <Note note={workoutTracker.note} />
      </div>
      <div className='flex items-center justify-between text-xs'>
        <div className='flex items-center justify-center'>
          <div className='text-white border rounded-full px-2 py-1 bg-accent/20 border-accent/80'>
            {workoutTracker.feeling
              ? workoutTracker.feeling.replaceAll(',', ' ')
              : workoutTracker.level
              ? `Lv. ${workoutTracker.level ?? 1}`
              : ''}
          </div>
        </div>
        <div className='flex items-center justify-center'>
          <div className='text-white border rounded-full px-2 py-1 bg-accent/20 border-accent/80'>
            {workoutTracker.logs.reduce((acc, cur) => acc + cur.sets.length, 0)}{' '}
            Sets
          </div>
        </div>
        <div className='flex items-center justify-center'>
          <div className='text-white border rounded-full px-2 py-1 bg-accent/20 border-accent/80'>
            {workoutTracker.duration
              ? Math.ceil(workoutTracker.duration / 60)
              : 0}{' '}
            min
          </div>
        </div>
        <div>
          <ChevronDownIcon
            className={`size-5 transition-transform duration-300 hover:cursor-pointer ${
              isOpen ? 'rotate-180' : ''
            }`}
            fill='white'
            onClick={handleOpen}
          />
        </div>
      </div>
    </div>
  );
};

export default HiitItem;
